import { Link } from "react-router-dom";
import { ArrowUpRight, Phone, Mail, Instagram, Facebook, Youtube, Twitter, Linkedin, Smartphone } from "lucide-react";
import {
  SHOP_ALL_URL,
  collections,
  companyLinks,
  partnerLinks,
  legalLinks,
  socials,
  contact,
} from "@/data/products";
import { AppStoreButtons } from "./AppStoreButtons";

const iconMap = {
  instagram: Instagram,
  facebook: Facebook,
  youtube: Youtube,
  twitter: Twitter,
  linkedin: Linkedin,
};

const LinkCol = ({ title, items, prefix }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.2em] text-[#f4f1eb]/50">{title}</div>
    <ul className="mt-5 space-y-3">
      {items.map((c) => (
        <li key={c.label}>
          <a
            href={c.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`footer-${prefix}-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
            className="text-[#f4f1eb]/80 hover:text-[#ff8a3d] transition-colors duration-300"
          >
            {c.label}
          </a>
        </li>
      ))}
    </ul>
  </div>
);

export const Footer = () => {
  return (
    <footer data-testid="main-footer" className="bg-[#1d231e] text-[#f4f1eb]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-20 pb-10">
        {/* Download the app callout */}
        <div
          data-testid="footer-app-download"
          className="mb-16 flex flex-col gap-8 rounded-3xl border border-white/10 bg-white/[0.03] p-8 sm:p-10 lg:flex-row lg:items-center lg:justify-between"
        >
          <div className="flex items-center gap-5">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#e85d04] text-white">
              <Smartphone size={26} />
            </span>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#ff8a3d]">Now available</div>
              <div className="font-serif-display text-2xl sm:text-3xl tracking-tight">
                Download the Alpha Omega Vitality App
              </div>
              <p className="mt-2 max-w-md text-sm text-[#f4f1eb]/60">
                Personalized wellness, supplement tracking, and exclusive offers — in your pocket.
              </p>
            </div>
          </div>
          <AppStoreButtons dark className="lg:justify-end" />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-12 gap-10 border-b border-white/10 pb-16">
          <div className="col-span-2 md:col-span-4">
            <img
              src="/logo-footer.png"
              alt="Alpha Omega Vitality logo"
              className="h-20 sm:h-24 w-auto"
              data-testid="footer-logo"
            />
            <p className="mt-6 max-w-xs text-[#f4f1eb]/70 leading-relaxed">
              Clinical precision. Natural alchemy. Premium vitamins and
              supplements, formulated at research doses and third-party tested.
            </p>
            <Link
              to="/journal"
              data-testid="footer-journal-link"
              className="mt-5 inline-flex items-center gap-2 text-[#ff8a3d] hover:text-white transition-colors duration-300"
            >
              Read the Journal <ArrowUpRight size={15} />
            </Link>
            <Link
              to="/catalog"
              data-testid="footer-catalog-link"
              className="mt-2 flex items-center gap-2 text-[#ff8a3d] hover:text-white transition-colors duration-300"
            >
              View the Product Catalog <ArrowUpRight size={15} />
            </Link>
            <div className="mt-6 space-y-2">
              <a
                href={contact.phoneHref}
                data-testid="footer-phone"
                className="flex items-center gap-3 text-[#f4f1eb]/80 hover:text-[#ff8a3d] transition-colors duration-300"
              >
                <Phone size={16} /> {contact.phone}
              </a>
              <a
                href={contact.emailHref}
                data-testid="footer-email"
                className="flex items-center gap-2.5 text-sm whitespace-nowrap text-[#f4f1eb]/80 hover:text-[#ff8a3d] transition-colors duration-300"
              >
                <Mail size={15} className="shrink-0" /> {contact.email}
              </a>
            </div>
            <div className="mt-6 flex items-center gap-3">
              {socials.map((s) => {
                const Icon = iconMap[s.icon];
                return (
                  <a
                    key={s.label}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    data-testid={`footer-social-${s.icon}`}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-[#f4f1eb]/80 hover:bg-[#e85d04] hover:border-[#e85d04] hover:text-white transition-colors duration-300"
                  >
                    <Icon size={17} />
                  </a>
                );
              })}
            </div>
          </div>

          <div className="md:col-span-2 md:col-start-6">
            <LinkCol title="Collections" items={collections} prefix="collection" />
          </div>
          <div className="md:col-span-2">
            <LinkCol title="Company" items={companyLinks} prefix="company" />
          </div>
          <div className="md:col-span-2">
            <LinkCol title="Partners" items={partnerLinks} prefix="partner" />
          </div>
        </div>

        <div className="flex flex-wrap gap-x-6 gap-y-3 py-8 border-b border-white/10">
          {legalLinks.map((l) => (
            <a
              key={l.label}
              href={l.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`footer-legal-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
              className="text-sm text-[#f4f1eb]/60 hover:text-[#ff8a3d] transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
          <Link
            to="/account-deletion/alphapulse"
            data-testid="footer-legal-delete-alphapulse"
            className="text-sm text-[#f4f1eb]/60 hover:text-[#ff8a3d] transition-colors duration-300"
          >
            Delete AlphaPulse Account
          </Link>
          <Link
            to="/account-deletion/nutrivue"
            data-testid="footer-legal-delete-nutrivue"
            className="text-sm text-[#f4f1eb]/60 hover:text-[#ff8a3d] transition-colors duration-300"
          >
            Delete NutriVue Account
          </Link>
          <Link
            to="/privacy/nutrivue"
            data-testid="footer-legal-privacy-nutrivue"
            className="text-sm text-[#f4f1eb]/60 hover:text-[#ff8a3d] transition-colors duration-300"
          >
            NutriVue Privacy Policy
          </Link>
          <Link
            to="/terms/nutrivue"
            data-testid="footer-legal-terms-nutrivue"
            className="text-sm text-[#f4f1eb]/60 hover:text-[#ff8a3d] transition-colors duration-300"
          >
            NutriVue Terms
          </Link>
          <Link
            to="/privacy/alphapulse"
            data-testid="footer-legal-privacy-alphapulse"
            className="text-sm text-[#f4f1eb]/60 hover:text-[#ff8a3d] transition-colors duration-300"
          >
            AlphaPulse Privacy Policy
          </Link>
          <Link
            to="/terms/alphapulse"
            data-testid="footer-legal-terms-alphapulse"
            className="text-sm text-[#f4f1eb]/60 hover:text-[#ff8a3d] transition-colors duration-300"
          >
            AlphaPulse Terms
          </Link>
        </div>

        <div className="flex flex-col sm:flex-row justify-between gap-4 pt-12 text-sm text-[#f4f1eb]/50">
          <p>© {new Date().getFullYear()} Alpha Omega Vitality. All rights reserved.</p>
          <p className="max-w-xl">
            These statements have not been evaluated by the FDA. Products are not
            intended to diagnose, treat, cure or prevent any disease.
          </p>
        </div>
      </div>
    </footer>
  );
};
